// Congratulatory phrases and proverbs per language for the summary screen

export interface SummaryPhrase {
  text: string;
  translation: string;
  // English sibling of `translation` — optional, filled in for languages
  // whose content is bilingual (Kama, Swahili). Falls back to `translation`
  // when absent (see src/utils/localizedContent.ts's getWordTranslation).
  translationEn?: string;
  // Kama-only: real hieroglyphs for phrases whose sign-by-sign spelling is
  // already verified elsewhere in the Kama corpus (never guessed here —
  // see the kama-hieroglyph-decoder skill). Omitted when no confidently
  // verified spelling exists (e.g. the "ꜥnḫ wḏꜣ snb" formula below).
  illustration?: string;
}

export const CONGRATS: Record<string, SummaryPhrase[]> = {
  ba: [
    { text: 'I ni ce !',        translation: 'littéralement : « toi et l\'effort »', translationEn: 'literally: "you and the effort"' },
    { text: 'I ka di !',        translation: 'Tu es fort !', translationEn: 'You are strong!' },
    { text: 'I tɛ jɛ !',       translation: 'Tu n\'as pas abandonné !', translationEn: 'You didn\'t give up!' },
  ],
  wf: [
    { text: 'Dëgg nga !',       translation: 'Tu as bien fait !', translationEn: 'You did well!' },
    { text: 'Jerejef !',        translation: 'Merci — et bravo !', translationEn: 'Thank you — and well done!' },
    { text: 'Yoboy dëgg !',     translation: 'Tu as réussi !', translationEn: 'You succeeded!' },
  ],
  la: [
    { text: 'Osali malamu !',   translation: 'Tu as bien travaillé !', translationEn: 'You worked well!' },
    { text: 'Matondi !',        translation: 'Félicitations !', translationEn: 'Congratulations!' },
    { text: 'Ozali makasi !',   translation: 'Tu es fort !', translationEn: 'You are strong!' },
  ],
  ff: [
    { text: 'Jaaraama !',       translation: 'Merci — et félicitations !', translationEn: 'Thank you — and congratulations!' },
    { text: 'Aan huli !',       translation: 'Tu as réussi !', translationEn: 'You succeeded!' },
    { text: 'Ko heewi !',       translation: 'C\'est suffisant !', translationEn: 'That\'s enough!' },
  ],
  sw: [
    { text: 'Hongera !',        translation: 'Félicitations !', translationEn: 'Congratulations!' },
    { text: 'Umefanya vizuri !',translation: 'Tu as bien fait !', translationEn: 'You did well!' },
    { text: 'Endelea !',        translation: 'Continue !', translationEn: 'Keep going!' },
  ],
  co: [
    { text: 'Ⲁϣⲁⲓ ⲛⲁⲕ !',   translation: 'Félicitations !', translationEn: 'Congratulations!' },
    { text: 'Ⲡⲉⲕϩⲏⲧ ⲛⲁϩⲙⲉⲕ !', translation: 'Ton cœur sera fort !', translationEn: 'Your heart will be strong!' },
  ],
  yo: [
    { text: 'O káre !',         translation: 'Bien joué !', translationEn: 'Well done!' },
    { text: 'Ẹ kú iṣẹ́ o !',     translation: 'Bravo pour ton travail !', translationEn: 'Well done on your work!' },
    { text: 'Tẹ̀síwájú !',       translation: 'Continue !', translationEn: 'Keep going!' },
  ],
  ka: [
    { text: 'Ꜥnḫ, wḏꜣ, snb !', translation: 'Vie, prospérité, santé ! — la formule de bon augure la plus employée de toute l\'Égypte ancienne', translationEn: 'Life, prosperity, health! — the most widely used formula of good fortune in all of ancient Egypt' },
    { text: 'Nfr nfr !',        translation: 'Très bien, excellent ! (nfr redoublé pour l\'emphase)', translationEn: 'Very good, excellent! (nfr doubled for emphasis)', illustration: '𓄤𓄤' },
  ],
};

export const PROVERBS: Record<string, SummaryPhrase[]> = {
  ba: [
    { text: 'Kuma ye bɛɛ fa di.',                   translation: 'La parole est le père de tous.', translationEn: 'Speech is the father of all.' },
    { text: 'Dɔɔnin dɔɔnin ko laga.',               translation: 'Petit à petit on remplit la calebasse.', translationEn: 'Little by little the calabash gets filled.' },
    { text: 'Sani i ka taa, i k\'i taa ka lɔ.',      translation: 'Avant de partir, commence par partir (vis l\'instant).', translationEn: 'Before you leave, start by leaving (live the moment).' },
    { text: 'Mɔgɔ ka taa waati bɛ se ka kɔ.',        translation: 'L\'heure du départ de l\'homme peut se rattraper.', translationEn: 'A person\'s time of departure can be made up for.' },
    { text: 'Nɔgɔya tɛ tile kelen na.',              translation: 'On ne réussit pas en un seul jour.', translationEn: 'Success doesn\'t come in a single day.' },
  ],
  wf: [
    { text: 'Ndank ndank mooy jàpp golo ci nay.',    translation: 'Doucement on attrape le singe dans la forêt.', translationEn: 'Slowly, one catches the monkey in the forest.' },
    { text: 'Bëgg bëgg amul baraka.',                translation: 'Le désir seul n\'apporte pas la bénédiction.', translationEn: 'Desire alone brings no blessing.' },
    { text: 'Lu dul dem, du ñëw.',                   translation: 'Ce qui ne part pas ne peut pas revenir.', translationEn: 'What doesn\'t leave cannot come back.' },
    { text: 'Dëkk bi dafa am doom yi.',              translation: 'Le village appartient à ses enfants.', translationEn: 'The village belongs to its children.' },
    { text: 'Ku amul xam-xam, amul dëkk.',           translation: 'Celui qui n\'a pas de savoir n\'a pas de pays.', translationEn: 'He who has no knowledge has no country.' },
  ],
  la: [
    { text: 'Moto na moto na bolingo.',              translation: 'Chaque personne a besoin d\'amour.', translationEn: 'Every person needs love.' },
    { text: 'Elima eyei moke moke.',                 translation: 'La connaissance vient peu à peu.', translationEn: 'Knowledge comes little by little.' },
    { text: 'Bolingo ezali nguya.',                  translation: 'L\'amour est une force.', translationEn: 'Love is a force.' },
    { text: 'Mutu akufi te na kaka mosala.',         translation: 'L\'homme ne se fatigue pas du seul travail.', translationEn: 'A person is not worn out by work alone.' },
  ],
  ff: [
    { text: 'Goonga foti nebbataake.',               translation: 'La vérité ne se tait pas longtemps.', translationEn: 'The truth does not stay silent for long.' },
    { text: 'Ko heewi fof, heen muuñal.',            translation: 'Tout ce qui est trop, mets-le de côté.', translationEn: 'Whatever is too much, set it aside.' },
    { text: 'Hannde ko hanki janngi.',               translation: 'Aujourd\'hui c\'est hier qui a appris.', translationEn: 'Today is what yesterday taught.' },
    { text: 'Seeɗa seeɗa laaɓata.',                 translation: 'Peu à peu, les choses s\'éclaircissent.', translationEn: 'Little by little, things become clear.' },
  ],
  sw: [
    { text: 'Haraka haraka haina baraka.',           translation: 'La précipitation n\'apporte pas de bénédiction.', translationEn: 'Haste brings no blessing.' },
    { text: 'Haba na haba hujaza kibaba.',           translation: 'Peu à peu on remplit la mesure.', translationEn: 'Little by little fills the measure.' },
    { text: 'Asiyesikia la mkubwa, huvunjika guu.',  translation: 'Celui qui n\'écoute pas les anciens se casse la jambe.', translationEn: 'He who doesn\'t heed the elders\' advice breaks his leg.' },
    { text: 'Elimu haina mwisho.',                   translation: 'L\'éducation n\'a pas de fin.', translationEn: 'Education has no end.' },
  ],
  co: [
    { text: 'Ⲡⲓⲟⲩⲱϣ ⲉϥⲥⲙⲁⲙⲁⲁⲧ.',              translation: 'La connaissance est une bénédiction.', translationEn: 'Knowledge is a blessing.' },
    { text: 'Ⲙⲉⲣⲓ ⲡⲉⲕⲥⲟⲛ.',                       translation: 'Aime ton prochain.', translationEn: 'Love your neighbor.' },
  ],
  yo: [
    { text: 'Ìwà l’ẹwà.',                            translation: 'Le caractère, c’est la beauté.', translationEn: 'Character is beauty.' },
    { text: 'Díẹ̀díẹ̀ nimú ẹlẹ́dẹ̀ ń wọgbà.',           translation: 'Petit à petit, le groin du cochon entre dans le jardin.', translationEn: 'Little by little, the pig’s snout gets into the garden.' },
    { text: 'Àgbájọ ọwọ́ la fi ń sọ̀yà.',             translation: 'C’est avec toutes les mains réunies qu’on se frappe la poitrine (l’union fait la force).', translationEn: 'It takes all hands together to beat one’s chest (unity is strength).' },
  ],
  // Pas de tradition de proverbes populaires attestée pour l'égyptien
  // classique comme pour les langues à tradition orale ci-dessus — plutôt
  // que d'inventer un proverbe non attesté, on reprend ici des tournures et
  // épithètes réellement attestées et déjà vérifiées dans ce corpus (voir
  // les fiches « nṯr nfr » et « Ordre verbe-sujet-objet » dans Phrase).
  ka: [
    { text: 'Nṯr nfr.',       translation: '« Le bon dieu » — l\'une des épithètes royales les plus employées sur les monuments égyptiens', translationEn: '"The good god" — one of the most commonly used royal epithets on Egyptian monuments', illustration: '𓊹𓄤' },
    { text: 'Sḏm zẖꜣw mdt.',  translation: '« Le scribe entend les mots » — l\'ordre verbe-sujet-objet de l\'égyptien classique, à l\'œuvre dans sa phrase la plus simple', translationEn: '"The scribe hears the words" — the verb-subject-object order of Classical Egyptian, at work in its simplest sentence', illustration: '𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏' },
    { text: 'Zẖꜣw pw.',       translation: '« C\'est un scribe » — la phrase nominale égyptienne : aucun verbe « être » n\'est nécessaire', translationEn: '"He is a scribe" — the Egyptian nominal sentence: no verb "to be" is needed', illustration: '𓏞𓅱𓀀𓊪𓅱' },
  ],
};

export function getRandomCongrats(langCode: string): SummaryPhrase {
  const list = CONGRATS[langCode] ?? CONGRATS['ba'];
  return list[Math.floor(Math.random() * list.length)];
}

export function getRandomProverb(langCode: string): SummaryPhrase {
  const list = PROVERBS[langCode] ?? PROVERBS['ba'];
  return list[Math.floor(Math.random() * list.length)];
}

export const SENTIER_NAMES: Record<string, string> = {
  ba: 'Sira',         // Bambara   — pas d'article (langue isolante)
  wf: 'Yoon wi',      // Wolof     — article postposé classe W
  la: 'Nzela',        // Lingala   — pas d'article
  ff: 'Laawol ngol',  // Peul      — article postposé classe ol
  sw: 'Njia',         // Swahili   — pas d'article
  co: 'ⲡⲙⲁⲓⲧ',      // Copte     — article préposé masc. ⲡ- (pe)
  yo: 'Ọ̀nà',         // Yoruba    — pas d'article
};

export function getSentierName(langCode: string): string {
  return SENTIER_NAMES[langCode] ?? 'le Sentier';
}
